// ─── PCM plumbing ────────────────────────────────────────────────────────────
// Everything between Web Audio's Float32 world and the protocol's little-endian
// pcm16 frames. Pure functions over typed arrays: no Web Audio, no DOM, no clock —
// which is what makes the mic path unit-testable.

/** Clamp-and-scale to signed 16-bit. Out-of-range input clips rather than wraps. */
export function floatToPcm16(samples: Float32Array): Int16Array {
  const out = new Int16Array(samples.length)
  for (let i = 0; i < samples.length; i++) {
    const s = samples[i]
    out[i] = s <= -1 ? -32768 : s >= 1 ? 32767 : Math.round(s * (s < 0 ? 32768 : 32767))
  }
  return out
}

/** Bytes off the wire → Float32 for an AudioBuffer. A trailing odd byte is dropped. */
export function pcm16ToFloat(bytes: ArrayBufferLike): Float32Array {
  const view = new DataView(bytes)
  const count = Math.floor(bytes.byteLength / 2)
  const out = new Float32Array(count)
  for (let i = 0; i < count; i++) out[i] = view.getInt16(i * 2, true) / 32768
  return out
}

/**
 * Mic blocks → pcm16 at the rate the session declared.
 *
 * The AudioContext runs at whatever rate the device gives us, so a resample is
 * usually needed. Linear interpolation is enough for speech, but only if it is
 * continuous across block boundaries — so the fractional read position and the
 * previous block's last sample are carried over. Without that carry every 128-sample
 * quantum boundary is an audible click.
 */
export class MicEncoder {
  private readonly step: number
  /** Read position in the virtual array `[tail, ...block]`; 1 = the block's first sample. */
  private pos = 1
  private tail = 0

  constructor(
    readonly fromRate: number,
    readonly toRate: number,
  ) {
    if (!(fromRate > 0) || !(toRate > 0)) throw new Error('MicEncoder: rates must be positive')
    this.step = fromRate / toRate
  }

  get passthrough(): boolean {
    return this.step === 1
  }

  encode(block: Float32Array): Int16Array {
    return floatToPcm16(this.resample(block))
  }

  /** Exposed for tests and for level metering before quantisation. */
  resample(block: Float32Array): Float32Array {
    const n = block.length
    if (!n) return new Float32Array(0)

    const at = (i: number) => (i <= 0 ? this.tail : block[i - 1])
    const out = new Float32Array(Math.ceil((n + 1 - this.pos) / this.step))
    let count = 0
    let p = this.pos
    for (; p <= n; p += this.step) {
      const i = Math.floor(p)
      const frac = p - i
      out[count++] = frac === 0 ? at(i) : at(i) + (at(i + 1) - at(i)) * frac
    }

    this.pos = p - n
    this.tail = block[n - 1]
    return count === out.length ? out : out.subarray(0, count)
  }

  reset(): void {
    this.pos = 1
    this.tail = 0
  }
}
